import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { spamTriggerWords } from '../misc/spamTriggerWords';
import { DistributionService } from '../services/distribution.service';
import { DistributionModel } from '../../../Discover/Targeting/models/distribution.model';

export class HeaderAssessmentMessage {
    subjectLine: string[] = [];
    previewText: string[] = [];
    fromName: string[] = [];
    replyEmail: string[] = [];
}

@Injectable()
export class HeaderEditorService implements OnDestroy {
    distribution: DistributionModel;

    private headerAssessmentMessages: HeaderAssessmentMessage = new HeaderAssessmentMessage();
    private headerAssessmentMessages$: BehaviorSubject<HeaderAssessmentMessage> = new BehaviorSubject<HeaderAssessmentMessage>(null);
    subscriptions: Subscription[] = [];

    constructor(
        public distributionService: DistributionService,
        public localizationService: LocalizationService
    ) {
        this.subscriptions.push(
            this.distributionService.getFocusedResource().subscribe(distribution => {
                if (!distribution) {
                    return;
                }
                this.distribution = distribution;
                this.assessDistributionHeader();
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    getHeaderAssessmentMessages() {
        return this.headerAssessmentMessages$;
    }

    updateHeader(assessmentMessages: HeaderAssessmentMessage): void {
        this.headerAssessmentMessages = assessmentMessages;
        this.headerAssessmentMessages$.next(this.headerAssessmentMessages);
    }

    assessDistributionHeader() {
        this.headerAssessmentMessages.subjectLine = this.assessSubjectLine(this.distribution.subjectLine);
        this.headerAssessmentMessages.previewText = this.assessPreviewText(this.distribution.previewText, this.distribution.subjectLine);
        this.headerAssessmentMessages.fromName = this.assessFromName(this.distribution.fromName);
        this.headerAssessmentMessages.replyEmail = this.assessReplyEmail(this.distribution.replyEmail);
        this.headerAssessmentMessages$.next(this.headerAssessmentMessages);
    }

    assessSubjectLine(subjectLine: string): string[] {
        const messages: string[] = [];
        if (subjectLine) {
            if (subjectLine.split(' ').length > 10 || subjectLine.length > 60) {
                messages.push(this.localizationService
                    .transform('Discover.targeting.distribution.headerEditor.assessment.subjectLine.tooLong'));
            }
            const spamWords = spamTriggerWords.filter(x => {
                const foundIndex = subjectLine.toLowerCase().indexOf(x.toLowerCase());
                if (foundIndex > -1) {
                    return (
                        (foundIndex === 0 || subjectLine[foundIndex - 1] === ' ')
                        && (foundIndex + x.length === subjectLine.length || subjectLine[foundIndex + x.length] === ' ')
                    );
                }
            });
            if (spamWords.length > 0) {
                messages.push(this.localizationService
                    .transform('Discover.targeting.distribution.headerEditor.assessment.subjectLine.containsSpamTriggerWords', {
                        spamTriggerWords: spamWords.map(word => subjectLine.substr(
                            subjectLine.toLowerCase().indexOf(word.toLowerCase()), word.length)).join(', ')
                    })
                );
            }
        }

        return messages;
    }

    assessPreviewText(previewText: string, subjectLine: string): string[] {
        const messages: string[] = [];
        if (!previewText) {
            messages.push(this.localizationService
                .transform('Discover.targeting.distribution.headerEditor.assessment.previewText.useField'));
        } else if (subjectLine && (previewText.toLowerCase() === subjectLine.toLowerCase())) {
            messages.push(this.localizationService
                .transform('Discover.targeting.distribution.headerEditor.assessment.previewText.differentFromSubjectLine'));
        }

        return messages;
    }

    assessFromName(fromName: string): string[] {
        const messages: string[] = [];
        if (fromName) {
            if (fromName.length > 20) {
                messages.push(this.localizationService
                    .transform('Discover.targeting.distribution.headerEditor.assessment.fromName.tooLong'));
            }
            if (fromName.search(/(.+)@(.+){2,}\.(.+){2,}/) > -1) {
                messages.push(this.localizationService
                    .transform('Discover.targeting.distribution.headerEditor.assessment.fromName.noEmail'));
            }
        }

        return messages;
    }

    assessReplyEmail(replyEmail: string): string[] {
        const messages: string[] = [];
        const noReplyEmail: string[] = ['no-reply@', 'noreply@', 'do-not-reply@', 'donotreply@'];
        if (replyEmail && noReplyEmail.find(x => replyEmail.startsWith(x))) {
            messages.push(this.localizationService
                .transform('Discover.targeting.distribution.headerEditor.hints.replyEmail.1'));
        }
        const sendFromEmail = this.distribution?.sendFrom?.connectionEmail;
        if (replyEmail && sendFromEmail && sendFromEmail.toLowerCase() !== replyEmail.toLowerCase()) {
            messages.push(this.localizationService
                .transform('Discover.targeting.distribution.headerEditor.hints.replyEmail.mismatchSendFrom'));
        }

        return messages;
    }
}
